import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import SEO from "../seo/SEO";

import BlogCard from "../components/Blog/BlogCard";
import BlogSidebar from "../components/Blog/BlogSidebar";
import Newsletter from "../components/Blog/Newsletter";

import "../styles/components/BlogPage.css";

import crmFutureImage from "../assets/images/blog/crm-future.jpg"
import ecommerce_migration from "../assets/images/blog/ecommerce-migration.jpg"
import mobile_security from "../assets/images/blog/mobile-security.jpg"
import crm_comparison from "../assets/images/blog/crm-comparison.jpg"
import pwa from "../assets/images/blog/pwa.jpg"
import checkoutOptimization from "../assets/images/blog/checkout-optimization.png"

/* =========================================================
   BLOG POSTS
========================================================= */

const blogPosts = [
  {
    id: 1,

    title: "Custom CRM Software: Features Modern Businesses Need",

    excerpt:
      "Explore the essential CRM features businesses need for lead management, sales tracking, customer workflows, automation and reporting.",

    category: "CRM",

    date: "August 20, 2026",

    readTime: "6 min",

    image: crmFutureImage,

    slug: "custom-crm-software-features",
  },

  {
    id: 2,

    title: "Ecommerce Platform Development: Architecture & Best Practices",

    excerpt:
      "Learn how scalable ecommerce platforms are designed using modern frontend frameworks, APIs, payment systems, databases and cloud infrastructure.",

    category: "Ecommerce",

    date: "August 12, 2026",

    readTime: "8 min",

    image: ecommerce_migration,

    slug: "ecommerce-platform-development-best-practices",
  },

  {
    id: 3,

    title: "Mobile App Security: Protecting Customer and Business Data",

    excerpt:
      "Essential security practices for Android, iOS and cross-platform mobile applications handling customer and business information.",

    category: "Mobile",

    date: "August 5, 2026",

    readTime: "6 min",

    image: mobile_security,

    slug: "mobile-app-security",
  },

  {
    id: 4,

    title: "Custom CRM vs Off-the-Shelf CRM: Which Is Better?",

    excerpt:
      "Compare custom CRM development with ready-made CRM platforms to understand which approach fits your workflows, budget and long-term business goals.",

    category: "CRM",

    date: "July 28, 2026",

    readTime: "7 min",

    image: crm_comparison,

    slug: "custom-crm-vs-off-the-shelf",
  },

  {
    id: 5,

    title: "Mobile App Development for Business Automation",

    excerpt:
      "Discover how custom mobile applications can automate field operations, customer services, sales workflows and internal business processes.",

    category: "Mobile",

    date: "July 20, 2026",

    readTime: "6 min",

    image: pwa,

    slug: "mobile-app-development-business-automation",
  },

  {
    id: 6,

    title: "How to Optimize Ecommerce Checkout for Higher Conversions",

    excerpt:
      "Practical ecommerce development strategies for improving checkout experience, reducing cart abandonment and increasing completed orders.",

    category: "Ecommerce",

    date: "July 12, 2026",

    readTime: "6 min",

    image: checkoutOptimization,

    slug: "ecommerce-checkout-optimization",
  },
];

/* =========================================================
   BLOG PAGE
========================================================= */

const BlogPage = () => {
  const [posts, setPosts] = useState([]);

  const [filteredPosts, setFilteredPosts] = useState([]);

  const [activeCategory, setActiveCategory] =
    useState("all");

  const [searchQuery, setSearchQuery] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  /* =======================================================
     SEO STRUCTURED DATA
  ======================================================= */

  const blogSchema = {
    "@context": "https://schema.org",

    "@type": "Blog",

    name: "Bairagi Consultants Software Development Blog",

    url: "https://www.bairagi-consultants.com/blog",

    description:
      "Software development insights covering custom software, ecommerce development, mobile applications, CRM, MIS, AI solutions and business automation.",

    publisher: {
      "@type": "Organization",

      name: "Bairagi Consultants",

      url: "https://www.bairagi-consultants.com",

      logo: {
        "@type": "ImageObject",

        url:
          "https://www.bairagi-consultants.com/logo512.png",
      },
    },
  };

  /* =======================================================
     LOAD BLOG POSTS
  ======================================================= */

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        /*
         * Currently using local blog data.
         *
         * This can later be replaced with:
         *
         * const response =
         *   await axios.get("/api/blog");
         *
         * const data = response.data;
         */

        setPosts(blogPosts);

        setFilteredPosts(blogPosts);

        setLoading(false);
      } catch (error) {
        console.error(
          "Error fetching blog posts:",
          error
        );

        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  /* =======================================================
     FILTER POSTS
  ======================================================= */

  useEffect(() => {
    let result = posts;

    if (activeCategory !== "all") {
      result = result.filter(
        (post) =>
          post.category === activeCategory
      );
    }

    if (searchQuery.trim()) {
      const query =
        searchQuery.toLowerCase().trim();

      result = result.filter(
        (post) =>
          post.title
            .toLowerCase()
            .includes(query) ||
          post.excerpt
            .toLowerCase()
            .includes(query) ||
          post.category
            .toLowerCase()
            .includes(query)
      );
    }

    setFilteredPosts(result);
  }, [
    activeCategory,
    searchQuery,
    posts,
  ]);

  /* =======================================================
     CATEGORIES
  ======================================================= */

  const categories = [
    "all",
    "CRM",
    "Ecommerce",
    "Mobile",
  ];

  return (
    <>
      {/* =================================================
          SEO
      ================================================= */}

      <SEO
        title="Software Development Blog | Bairagi Consultants"
        description="Software development insights from Bairagi Consultants covering custom software, ecommerce development, mobile apps, CRM, MIS, AI solutions and business automation."
        path="/blog"
        schema={blogSchema}
      />

      <div className="blog-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="blog-hero">

          <div className="container">

            <motion.div
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
              }}
            >

              <p className="blog-hero-label">
                SOFTWARE DEVELOPMENT INSIGHTS
              </p>

              <h1>
                Software Development
                <br />

                <span>
                  Insights & Resources
                </span>
              </h1>

              <p className="subtitle">

                Practical insights on custom software
                development, ecommerce platforms,
                mobile applications, CRM, MIS,
                artificial intelligence and
                business automation.

              </p>

            </motion.div>

          </div>

        </section>

        {/* =================================================
            MAIN BLOG CONTENT
        ================================================= */}

        <section className="blog-main">

          <div className="container">

            <div className="blog-layout">

              {/* =============================================
                  BLOG POSTS
              ============================================= */}

              <div className="blog-posts">

                {/* ===========================================
                    SEARCH + FILTER
                =========================================== */}

                <div className="blog-filters">

                  <div className="search-box">

                    <input
                      type="search"
                      placeholder="Search software development articles..."
                      value={searchQuery}
                      onChange={(e) =>
                        setSearchQuery(
                          e.target.value
                        )
                      }
                      aria-label="Search blog articles"
                    />

                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      aria-hidden="true"
                    >

                      <circle
                        cx="11"
                        cy="11"
                        r="8"
                      />

                      <path
                        d="M21 21l-4.35-4.35"
                      />

                    </svg>

                  </div>

                  {/* CATEGORY FILTERS */}

                  <div className="category-filters">

                    {categories.map(
                      (category) => (

                        <button
                          key={category}
                          type="button"
                          className={
                            activeCategory ===
                            category
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            setActiveCategory(
                              category
                            )
                          }
                          aria-pressed={
                            activeCategory ===
                            category
                          }
                        >

                          {category === "all"
                            ? "All Articles"
                            : category}

                        </button>

                      )
                    )}

                  </div>

                </div>

                {/* ===========================================
                    LOADING
                =========================================== */}

                {loading && (

                  <div className="loading-posts">

                    <div className="spinner"></div>

                    <p>
                      Loading articles...
                    </p>

                  </div>

                )}

                {/* ===========================================
                    NO RESULTS
                =========================================== */}

                {!loading &&
                  filteredPosts.length === 0 && (

                    <div className="no-results">

                      <h2>
                        No articles found
                      </h2>

                      <p>
                        Try adjusting your
                        search or category filter.
                      </p>

                    </div>

                  )}

                {/* ===========================================
                    POSTS
                =========================================== */}

                {!loading &&
                  filteredPosts.length > 0 && (

                    <div className="posts-grid">

                      {filteredPosts.map(
                        (post, index) => (

                          <motion.div
                            key={post.id}
                            initial={{
                              opacity: 0,
                              y: 20,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                            }}
                            transition={{
                              duration: 0.5,
                              delay:
                                index * 0.08,
                            }}
                          >

                            <BlogCard
                              post={post}
                            />

                          </motion.div>

                        )
                      )}

                    </div>

                  )}

                {/* ===========================================
                    PAGINATION
                =========================================== */}

                {!loading &&
                  filteredPosts.length > 0 && (

                    <div className="blog-pagination">

                      <button
                        type="button"
                        className="active"
                      >
                        1
                      </button>

                    </div>

                  )}

              </div>

              {/* =============================================
                  SIDEBAR
              ============================================= */}

              <BlogSidebar
                popularPosts={posts.slice(
                  0,
                  3
                )}
                categories={categories.filter(
                  (category) =>
                    category !== "all"
                )}
              />

            </div>

          </div>

        </section>

        {/* =================================================
            SEO INTERNAL LINKS
        ================================================= */}

        <section className="blog-services-section">

          <div className="container">

            <div className="blog-services-card">

              <h2>
                Explore Our Software Development Services
              </h2>

              <p>
                Looking for a technology partner?
                Explore our custom software,
                ecommerce, mobile application,
                CRM and AI development services.
              </p>

              <div className="blog-service-links">

                <Link to="/ecommerce-development">
                  Ecommerce Development
                </Link>

                <Link to="/mobile-app-development">
                  Mobile App Development
                </Link>

                <Link to="/crm-software-development">
                  CRM Development
                </Link>

                <Link to="/ai-software-solutions">
                  AI Software Solutions
                </Link>

                <Link to="/custom-dashboard-development">
                  MIS & Dashboard Development
                </Link>

              </div>

            </div>

          </div>

        </section>

        {/* =================================================
            NEWSLETTER
        ================================================= */}

        <Newsletter />

      </div>
    </>
  );
};

export default BlogPage;